// Blip's conversations (docs/BLIP_HELPER_DESIGN.md, "Conversations"): one for
// each project, kept in this browser (IndexedDB), with the knowledge pack it
// started with and what his panel shows of it. They are not saved to the
// account. A conversation only grows: what was sent to Claude is never
// changed, since the API checks that it is as it was.
//
// chat: { project, id, pack, messages (as sent to the API), log (what the
// panel shows: [{ kind, ... }]), created, updated }

const DATABASE = "b3d-blip", STORE = "chats";

export class Chats {
  static async open() {
    const chats = new Chats();
    try {
      chats.db = await new Promise((resolve, reject) => {
        const request = indexedDB.open(DATABASE, 1);
        request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "project" });
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch {
      // A browser that keeps nothing: the conversations last until the page
      // closes.
      chats.db = null;
    }
    chats.memory = new Map();
    return chats;
  }

  async transaction(mode, work) {
    if (!this.db) return work(null);
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORE, mode);
      const request = work(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve(request ? request.result : undefined);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  async get(project) {
    if (!this.db) return this.memory.get(project) || null;
    return (await this.transaction("readonly", (store) => store.get(project))) || null;
  }

  async put(chat) {
    chat.updated = Date.now();
    if (!this.db) {
      this.memory.set(chat.project, chat);
      return;
    }
    await this.transaction("readwrite", (store) => store.put(chat));
  }

  async remove(project) {
    if (!this.db) {
      this.memory.delete(project);
      return;
    }
    await this.transaction("readwrite", (store) => store.delete(project));
  }
}

export function newChat(project, pack) {
  return { project, id: crypto.randomUUID(), pack, messages: [], log: [], created: Date.now(), updated: Date.now() };
}

// The most pictures a conversation holds, the user's and the tools' alike
// (the server's LIMITS.images).
export const CHAT_PICTURES = 24;

// The pictures in a message's content, those of tools' results too.
function contentPictures(content) {
  const pictures = [];
  for (const block of Array.isArray(content) ? content : []) {
    if (block.type === "image") pictures.push(block);
    else if (block.type === "tool_result") pictures.push(...contentPictures(block.content));
  }
  return pictures;
}

export function chatPictures(chat) {
  return chat ? chat.messages.reduce((count, message) => count + contentPictures(message.content).length, 0) : 0;
}

// A picture block's size in pixels, { width, height }, from its first bytes:
// PNG, JPEG and GIF; null for another.
export function pictureSize(block) {
  const data = block && block.source && typeof block.source.data === "string" ? block.source.data : "";
  let bytes;
  try {
    // Its first 48 KB: a whole number of base64's groups of four.
    bytes = Uint8Array.from(atob(data.slice(0, 1 << 16)), (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
  const word = (at) => (bytes[at] << 8) | bytes[at + 1];
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes.length >= 24) {
    return { width: (word(16) << 16 | word(18)) >>> 0, height: (word(20) << 16 | word(22)) >>> 0 };
  }
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes.length >= 10) {
    return { width: bytes[6] | bytes[7] << 8, height: bytes[8] | bytes[9] << 8 };
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    // The frame's header (SOF0 to SOF15, but not DHT, JPG or DAC).
    for (let at = 2; at + 9 < bytes.length && bytes[at] === 0xff;) {
      const marker = bytes[at + 1];
      if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
        return { width: word(at + 7), height: word(at + 5) };
      }
      at += 2 + word(at + 2);
    }
  }
  return null;
}

// A picture's tokens: about width x height / 750, of at most the 1.15
// million pixels Claude makes use of; about a thousand when its size is not
// known.
const pictureTokens = (block) => {
  const size = pictureSize(block);
  return size ? Math.ceil(Math.min(size.width * size.height, 1.15e6) / 750) : 1000;
};

// A rough size of a conversation, in tokens: about four characters each,
// and each picture by its size.
export function chatTokens(chat) {
  let characters = 0, pictures = 0;
  const count = (content) => {
    if (typeof content === "string") {
      characters += content.length;
      return;
    }
    for (const block of content || []) {
      if (block.type === "image") pictures += pictureTokens(block);
      else if (block.type === "tool_result") count(block.content);
      else characters += JSON.stringify(block).length;
    }
  };
  for (const message of chat.messages) count(message.content);
  return Math.round(characters / 4) + pictures;
}
